import { API_BASE } from '@/lib/api-url';

export { API_BASE };

import { computeMetricsStatus, NETWORK_THRESHOLDS, type ConnectionStatus } from '@cibli/shared';

export type { ConnectionStatus };

export interface NetworkSample {
  latencyMs: number | null; // null = failed ping, counts as packet loss
  at: number;
}

export interface SpeedTestResult {
  latencyMs: number;
  downloadMbps: number;
  uploadMbps: number | null;
  testedAt: Date;
}

export interface NetworkQuality {
  status: ConnectionStatus;
  latencyAvgMs: number | null;
  jitterMs: number | null;
  lossPercent: number;
  downloadMbps: number | null;
  uploadMbps: number | null;
}

export { NETWORK_THRESHOLDS };

export function computeNetworkQuality(
  samples: NetworkSample[],
  downloadMbps: number | null,
  uploadMbps: number | null,
): NetworkQuality {
  const total = samples.length;
  const latencies = samples
    .map(s => s.latencyMs)
    .filter((v): v is number => v !== null && !Number.isNaN(v));

  const lossPercent = total === 0 ? 0 : ((total - latencies.length) / total) * 100;
  const latencyAvgMs =
    latencies.length > 0 ? latencies.reduce((a, b) => a + b, 0) / latencies.length : null;

  let jitterMs: number | null = null;
  if (latencies.length >= 2) {
    const deltas = latencies.slice(1).map((v, i) => Math.abs(v - latencies[i]));
    jitterMs = deltas.reduce((a, b) => a + b, 0) / deltas.length;
  }

  const status =
    total > 0 && latencies.length === 0
      ? 'critical' // every ping failed → offline
      : (computeMetricsStatus({
          latencyMs: latencyAvgMs,
          jitterMs,
          lossPercent,
          downloadMbps,
          uploadMbps,
        }) ?? 'good');

  return { status, latencyAvgMs, jitterMs, lossPercent, downloadMbps, uploadMbps };
}

export async function ping(): Promise<number> {
  const start = performance.now();
  const res = await fetch(`${API_BASE}/health?_=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Ping failed: ${res.status}`);
  return performance.now() - start;
}

export async function downloadTest(
  bytes: number,
  onProgress?: (ratio: number) => void,
): Promise<number> {
  if (bytes <= 0) return 0;
  const start = performance.now();
  const res = await fetch(`${API_BASE}/speedtest/download?bytes=${bytes}&_=${Date.now()}`, {
    cache: 'no-store',
  });
  if (!res.ok || !res.body) throw new Error(`Download failed: ${res.status}`);

  const reader = res.body.getReader();
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    received += value.byteLength;
    onProgress?.(received / bytes);
  }
  const seconds = (performance.now() - start) / 1000;
  return seconds > 0 ? (received * 8) / seconds / 1_000_000 : 0;
}

// Note: fetch reports no upload progress without a ReadableStream body
// (duplex: 'half'), which Capacitor WebViews do not all support — the
// progress bar tracks the download phase only.
export async function uploadTest(bytes: number): Promise<number> {
  if (bytes <= 0) return 0;
  const payload = new Blob([new Uint8Array(bytes)], { type: 'application/octet-stream' });
  const start = performance.now();
  const res = await fetch(`${API_BASE}/speedtest/upload?_=${Date.now()}`, {
    method: 'POST',
    body: payload,
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  const seconds = (performance.now() - start) / 1000;
  return seconds > 0 ? (bytes * 8) / seconds / 1_000_000 : 0;
}

export async function runFullTest(
  downloadBytes: number,
  uploadBytes: number,
  onProgress?: (ratio: number) => void,
): Promise<SpeedTestResult> {
  const latencyMs = await ping();
  const downloadMbps = await downloadTest(downloadBytes, onProgress);
  const uploadMbps = await uploadTest(uploadBytes);
  return { latencyMs, downloadMbps, uploadMbps, testedAt: new Date() };
}

// ── Internet speed test (@cloudflare/speedtest) ────────────────────────────
// Uses the same engine that powers speed.cloudflare.com: multi-connection
// block-series with ascending sizes, p90 aggregation, anycast edge selection.
// This produces measurements comparable to Ookla and Google on the same link.
//
// Privacy note: Cloudflare collects aggregated network metrics (no PII).
// See: https://www.cloudflare.com/privacypolicy/

import SpeedTest from '@cloudflare/speedtest';

export interface InternetSpeedResult {
  downloadMbps: number;
  uploadMbps: number;
  latencyMs: number | null;
  jitterMs: number | null;
}

// Full test (manual button): ~25 MB total, reference-grade measurement.
const FULL_MEASUREMENTS: ConfigOptions['measurements'] = [
  { type: 'latency', numPackets: 10 },
  { type: 'download', bytes: 100_000, count: 2 },
  { type: 'download', bytes: 1_000_000, count: 4 },
  { type: 'download', bytes: 10_000_000, count: 2 },
  { type: 'upload', bytes: 100_000, count: 2 },
  { type: 'upload', bytes: 1_000_000, count: 4 },
];

// Periodic test (idle, every 30 min): ~4.5 MB, same methodology,
// bounded data budget (~215 MB/day for 24h of continuous monitoring).
const PERIODIC_MEASUREMENTS: ConfigOptions['measurements'] = [
  { type: 'latency', numPackets: 5 },
  { type: 'download', bytes: 100_000, count: 2 },
  { type: 'download', bytes: 1_000_000, count: 3 },
  { type: 'upload', bytes: 100_000, count: 2 },
  { type: 'upload', bytes: 1_000_000, count: 1 },
];

type ConfigOptions = import('@cloudflare/speedtest').ConfigOptions;

/**
 * Runs a Cloudflare speed test and returns the summary.
 * The engine handles multi-connection orchestration, TCP slow-start
 * bypass, and p90 aggregation automatically.
 */
export function measureInternetSpeed(mode: 'full' | 'periodic'): Promise<InternetSpeedResult> {
  return new Promise((resolve, reject) => {
    const engine = new SpeedTest({
      measurements: mode === 'full' ? FULL_MEASUREMENTS : PERIODIC_MEASUREMENTS,
      autoStart: false,
      measureDownloadLoadedLatency: false,
      measureUploadLoadedLatency: false,
      // Cloudflare results endpoint — sends aggregated metrics, no PII.
      logAimApiUrl: null,
    });

    engine.onError = (error: string) => {
      reject(new Error(`Internet speed test failed: ${error}`));
    };

    engine.onFinish = results => {
      const summary = results.getSummary();
      // getSummary() returns bits per second for bandwidth, ms for latency.
      resolve({
        downloadMbps: summary.download != null ? summary.download / 1e6 : 0,
        uploadMbps: summary.upload != null ? summary.upload / 1e6 : 0,
        latencyMs: summary.latency ?? null,
        jitterMs: summary.jitter ?? null,
      });
    };

    engine.play();
  });
}
